import { config } from '../config';
import { headers, APIResponse, SpanTag, stleaf, fetchAPI } from '../helpers';
import { CRS } from './crs';
/**
 * Clips an ortho and adds layer back into a mission
 * @param mediaId media id referring to ortho
 * @param annotationId annotation id to use as clip
 * @param missionId mission to put back ortho into
 */
export const clipOrtho = (
  st: SpanTag,
  mediaId: string,
  annotationId: string,
  missionId: string,
): Promise<APIResponse> =>
  fetchAPI(stleaf(st, 'rae3yo'), `${config.API_URL}/v1/transform/clip`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ mediaId, annotationId, missionId }),
  });

interface ExportOrthoData {
  mediaId: string;
  missionId: string;
  format: string | null; // it is never null, but have to set for types in ExportOrthoBox
  resolution: string | null; // it is never null, but have to set for types in ExportOrthoBox
  crs: CRS;
}

export const exportOrtho = (
  st: SpanTag,
  orthoId: string,
  data: ExportOrthoData,
): Promise<APIResponse> =>
  fetchAPI(stleaf(st, 'fei0uw'), `${config.API_URL}/v1/transform/export`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(data),
  });

type GridDiffData = {
  missionId: string;
  mediaIds: [string, string];
  colorRange: number;
  colorSchemeName: string;
};

/**
 * Create a Grid Diff from two DEM rasters
 * @param mediaIds an Array of two DEMs media ids
 * @param reportTitle name of the Report to be produced, used for exported file name
 * @param missionId mission to put back ortho into
 */
export const gridDiff = (
  st: SpanTag,
  data: GridDiffData,
): Promise<APIResponse> => {
  return fetchAPI(
    stleaf(st, 'demtr9'),
    `${config.API_URL}/v1/transform/griddiff`,
    {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(data),
    },
  );
};
type MapGridDiffData = {
  mapId: string;
  /** [comparison (later) mediaId, base (earlier) mediaId]. Output = comparison − base. */
  mediaIds: [string, string];
  /**
   * Opening style for the result layer: `[brewerSchemeName, interpolationSteps]`
   * (e.g. `['PuOr', '9']`) and the colour range in metres. All three are
   * editable on the layer afterwards.
   */
  colorMap?: [string, string];
  rangeMin?: number;
  rangeMax?: number;
  layerName?: string;
};

type MapContoursData = {
  mapId: string;
  /** The DEM (single-band COG) media to contour. */
  mediaId: string;
  /** Contour interval in the DEM's vertical units (metres for our DEMs). */
  interval: number;
  layerName?: string;
};

/**
 * Create a difference grid from two DEMs on a map. The result is added as a new
 * layer on `mapId`. `mediaIds` is [comparison, base] — output is comparison −
 * base, so positive = material added, negative = removed.
 */
export const mapGridDiff = (
  st: SpanTag,
  data: MapGridDiffData,
): Promise<APIResponse> =>
  fetchAPI(
    stleaf(st, 'mgddif'),
    `${config.API_URL}/v1/transform/map-griddiff`,
    {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(data),
    },
  );

/**
 * Generate contours from a DEM layer on a map (gdal_contour on the
 * transformer). The result is added to `mapId` as a new vector layer; the
 * requester is emailed when it's ready.
 */
export const mapContours = (
  st: SpanTag,
  data: MapContoursData,
): Promise<APIResponse<{ mediaId: string; layerId: string }>> =>
  fetchAPI(
    stleaf(st, 'mapcnt'),
    `${config.API_URL}/v1/transform/map-contours`,
    {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(data),
    },
  );

type MapHillshadeData = {
  mapId: string;
  /** The DEM (single-band COG) media to hillshade. */
  mediaId: string;
  layerName?: string;
};

/**
 * Generate a hillshade from a DEM layer on a map (gdaldem hillshade on the
 * transformer). The result is added to `mapId` as a new raster layer; the
 * requester is emailed when it's ready.
 */
export const mapHillshade = (
  st: SpanTag,
  data: MapHillshadeData,
): Promise<APIResponse<{ mediaId: string; layerId: string }>> =>
  fetchAPI(
    stleaf(st, 'maphsh'),
    `${config.API_URL}/v1/transform/map-hillshade`,
    {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(data),
    },
  );

interface ContourParameters {
  mediaId: string;
  missionId: string;
}

export const createContours = (
  st: SpanTag,
  parameters: ContourParameters,
): Promise<APIResponse> =>
  fetchAPI(stleaf(st, 'cont0r'), `${config.API_URL}/v1/transform/contours`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(parameters),
  });
